import BAULT_ABI from 'abis/Bault.json'
import { useMemo } from 'react'
import { Abi, Address } from 'viem'

import { useReadContractMultiple } from './useReadContractMultiple'

export interface BaultEarnedData {
  address: string
  earnedRaw: string | null // Raw earned value from contract
  earnedInWrapper: number | null // Scaled earned value using reward token price
  earnedInWrapperWithSlippage: number | null // Earned value with slippage
  isLoading: boolean
  error: boolean
}

/**
 * Custom hook to fetch real-time earned values for multiple baults using wagmi multicall
 * @param baultAddresses Array of bault contract addresses
 * @param rewardTokenPrice Reward token price for scaling calculation
 * @returns Array of earned data for each bault
 */
export function useBaultEarnedValues(
  baultAddresses: string[] | undefined,
  rewardTokenPrice: number | null
): BaultEarnedData[] {
  // Build contracts array
  const contracts = useMemo(() => {
    if (!baultAddresses) return []
    return baultAddresses.map((address) => ({
      address: address as Address,
      abi: BAULT_ABI as Abi,
      functionName: 'earned' as const,
    }))
  }, [baultAddresses])

  const {
    data: earnedResults,
    isLoading: isLoadingBatch,
    error: errorBatch,
  } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  // Process results and apply scaling formula
  return useMemo(() => {
    if (!baultAddresses) return []

    return baultAddresses.map((address, index) => {
      const result = earnedResults?.[index]
      // wagmi returns { status: 'success' | 'failure', result: ... } for each contract
      const isLoading = isLoadingBatch
      const error = errorBatch !== null || result?.status === 'failure'
      const earnedRaw = result?.status === 'success' && result?.result ? result.result.toString() : null

      let earnedInWrapper: number | null = null
      let earnedInWrapperWithSlippage: number | null = null

      // Apply scaling formula: earned reward amount * 0.9 * reward token price
      if (earnedRaw && rewardTokenPrice && !isLoading && !error) {
        try {
          const earnedInReward = parseFloat(earnedRaw) / 1e18 // Convert from wei to reward token amount
          earnedInWrapper = earnedInReward * 0.9 * rewardTokenPrice
          earnedInWrapperWithSlippage = (earnedInReward * 0.9 * rewardTokenPrice) / 1.03
        } catch (e) {
          console.warn(`Failed to calculate scaled earned value for bault ${address}:`, e)
        }
      }

      return {
        address,
        earnedRaw,
        earnedInWrapper,
        earnedInWrapperWithSlippage,
        isLoading,
        error,
      }
    })
  }, [baultAddresses, earnedResults, rewardTokenPrice, isLoadingBatch, errorBatch])
}

/**
 * Helper hook to get earned data for a single bault
 * @param baultAddress Single bault contract address
 * @param rewardTokenPrice Reward token price for scaling calculation
 * @returns Single bault earned data
 */
export function useBaultEarnedValue(
  baultAddress: string | undefined,
  rewardTokenPrice: number | null
): BaultEarnedData | null {
  const addresses = useMemo(() => (baultAddress ? [baultAddress] : undefined), [baultAddress])

  const results = useBaultEarnedValues(addresses, rewardTokenPrice)

  return results.length > 0 ? results[0] : null
}
